import { forwardRef, useState } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

// Brand colors - exact match with elka-manager
const BRAND_BLUE = '#3C5FA8';
const ERROR_RED = '#f43f5e';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, className, onFocus, onBlur, style, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    // Focus ring via inline style - guaranteed to work across all apps
    const focusRingColor = error ? ERROR_RED : BRAND_BLUE;
    const textareaStyle: React.CSSProperties = {
      ...(isFocused
        ? {
            boxShadow: `0 0 0 2px ${focusRingColor}`,
            borderColor: 'transparent',
            outline: 'none',
          }
        : {}),
      ...style,
    };

    return (
      <div className="w-full">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="mr-1 text-rose-500">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={clsx(
            'w-full rounded-xl border px-3 py-2',
            'focus:outline-none',
            'disabled:cursor-not-allowed disabled:bg-gray-100',
            'text-base',
            error ? 'border-rose-300' : 'border-[#E2E8F0] hover:border-[#C7D7F0]',
            className
          )}
          style={textareaStyle}
          {...props}
        />
        {error && <p className="mt-1 text-sm text-rose-600">{error}</p>}
        {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
